import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PhoneVerifyModal from '../components/PhoneVerifyModal';
import UserInfoModal from '../components/UserInfoModal';
import ReferralCodeModal from '../components/ReferralCodeModal';
import ReferralListModal from '../components/ReferralListModal';
import './SettingsPage.css';
import BaseLayout from '@/components/BaseLayout';
import { uploadAvatar } from '../services/apiService';
import { API_CONFIG } from '../config/apiConfig';

const SettingsPage: React.FC = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [showReferralCode, setShowReferralCode] = useState(false);
  const [showReferralList, setShowReferralList] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showReferralLink, setShowReferralLink] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // iOS Detection utility
  const isIOSDevice = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  const isSafariDesktop = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !/iPad|iPhone|iPod/.test(navigator.userAgent);
  };

  // Kiểm tra số điện thoại đã được xác thực hay chưa
  // Không phải số gen tạm và đã được verify
  const hasVerifiedPhone = !!(
    user?.phoneVerified && 
    user?.verifiedPhoneNumber && 
    user?.verifiedPhoneNumber !== '0000000000' &&
    user?.verifiedPhoneNumber.startsWith('+84')
  );

  // Check if user already has a referrer
  const hasReferrer = !!(user?.refBy);

  const handleAvatarClick = () => {
    if (isUploading) return;
    
    // iOS-specific handling for file input trigger
    if (isIOSDevice()) {
      // On iOS, we need to trigger the input in a more direct way
      // to avoid potential issues with the click event
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 100);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset previous states
    setUploadError('');
    setUploadSuccess(false);
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Vui lòng chọn file ảnh hợp lệ (PNG, JPG, JPEG, GIF)');
      return;
    }

    // iOS-specific file size validation (more restrictive for iOS)
    const isIOS = isIOSDevice();
    const isSafari = isSafariDesktop();
    const maxSize = (isIOS || isSafari) ? 3 * 1024 * 1024 : 5 * 1024 * 1024; // 3MB for iOS/Safari, 5MB for others
    
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      setUploadError(`Kích thước file không được vượt quá ${maxSizeMB}MB${isIOS ? ' (giới hạn đặc biệt cho iOS)' : ''}`);
      return;
    }

    setIsUploading(true);
    
    // iOS-specific optimizations
    let uploadPromise;
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      // Set different timeout for iOS (shorter due to memory constraints)
      const uploadTimeout = (isIOS || isSafari) ? 30000 : 60000; // 30s for iOS, 60s for others
      
      uploadPromise = uploadAvatar(file);
      
      // Create a timeout promise for iOS
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Upload timeout - vui lòng thử lại với file nhỏ hơn'));
        }, uploadTimeout);
      });

      // Race between upload and timeout
      await Promise.race([uploadPromise, timeoutPromise]);
      
      // Clear timeout if upload succeeds
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      await refreshUser(); // Refresh user to get the new avatar URL
      setUploadSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (error: any) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      console.error('Failed to upload avatar:', error);
      
      // iOS-specific error handling
      let errorMessage = 'Có lỗi xảy ra khi tải lên ảnh';
      
      if (error.message && error.message.includes('timeout')) {
        errorMessage = isIOS 
          ? 'Upload bị timeout trên iOS - vui lòng thử với file nhỏ hơn hoặc kết nối mạng tốt hơn'
          : 'Upload bị timeout - vui lòng thử lại';
      } else if (error.response?.status === 413) {
        errorMessage = 'File quá lớn. Vui lòng chọn file nhỏ hơn';
      } else if (error.response?.status === 400) {
        errorMessage = 'Định dạng file không được hỗ trợ';
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại';
      } else if (error.response?.status === 0 || error.code === 'NETWORK_ERROR') {
        errorMessage = isIOS 
          ? 'Lỗi kết nối mạng trên iOS - vui lòng kiểm tra kết nối và thử lại'
          : 'Lỗi kết nối mạng - vui lòng thử lại';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Upload bị hủy - vui lòng thử lại';
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Add iOS-specific error context
      if (isIOS && !error.message?.includes('iOS')) {
        errorMessage += ' (Lỗi có thể liên quan đến giới hạn iOS)';
      }
      
      setUploadError(errorMessage);
      
      // Hide error message after longer time for iOS users to read
      setTimeout(() => {
        setUploadError('');
      }, isIOS ? 7000 : 5000);
    } finally {
      setIsUploading(false);
      // Reset file input - important for iOS to allow re-selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // iOS-specific memory cleanup
      if (isIOS) {
        // Force garbage collection hint for iOS
        setTimeout(() => {
          if (window.gc) {
            window.gc();
          }
        }, 100);
      }
    }
  };

  const handlePhoneVerifySuccess = async () => {
    setShowPhoneVerify(false);
    // Refresh user data để cập nhật trạng thái phoneVerified
    await refreshUser();
  };

  const handleUserInfoSuccess = () => {
    setShowUserInfo(false);
    // Có thể refresh user data hoặc show success message
  };

  const handleReferralCodeSuccess = async () => {
    setShowReferralCode(false);
    // Refresh user data để cập nhật trạng thái refBy
    await refreshUser();
  };

  // Handle copy referral code
  const handleCopyReferralCode = async () => {
    if (!user?.referralCode) return;

    try {
      // Check if Clipboard API is available (modern browsers including mobile)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(user.referralCode);
        setCopySuccess(true);

        // Reset copy success message after 2 seconds
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
        return;
      }

      // Fallback method for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = user.referralCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.setAttribute('readonly', '');
      textArea.setAttribute('contenteditable', 'true');
      
      document.body.appendChild(textArea);
      
      // For mobile devices, we need to handle selection differently
      if (navigator.userAgent.match(/ipad|iphone/i)) {
        // iOS specific handling
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        // Android and other mobile devices
        textArea.select();
        textArea.setSelectionRange(0, 999999);
      }
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      } else {
        throw new Error('Copy command failed');
      }
      
    } catch (error) {
      console.error('Failed to copy referral link:', error);
      
      // Final fallback - show a modal with the link for manual copy
      setReferralLink(referralLink);
      setShowReferralLink(true);
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.email) return user.email;
    if (user?.verifiedPhoneNumber) return user.verifiedPhoneNumber;
    return 'Người dùng';
  };

  // Get user avatar
  const getUserAvatar = () => {
    const serverBaseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    const avatarSrc = user?.avatarUrl
      ? `${serverBaseUrl}${user.avatarUrl}`
      : "/images/icons/avatar_default.png";

    return (
      <img
        crossOrigin='anonymous'
        src={avatarSrc}
        alt="User Avatar"
        style={{ width: '100%', height: '100%', objectFit: 'cover'}}
      />
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('kiloba_has_visited');
      await logout();
      navigate('/'); // Navigate to home page after logout
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container" backgroundColor="transparent">
        <div className="settings-page">
          {/* Header with back button */}
          <div className='task-header'>
            <button className="task-back-btn" onClick={handleBackClick}>
              <img src="/images/icons/back.svg" alt="Back" />
            </button>
          </div>

          <div className='settings-page-content'>
            <div className='settings-main'>
              <div className="user-info-section">
              <div className="user-avatar">
                <div className="avatar-placeholder">
                  {getUserAvatar()}
                  {isUploading && (
                    <div className="upload-overlay">
                      <div className="upload-spinner"></div>
                      <span className="upload-text">Đang tải lên...</span>
                    </div>
                  )}
                </div>
                <div 
                  className={`avatar-edit-button ${isUploading ? 'disabled' : ''}`} 
                  onClick={handleAvatarClick}
                  style={{ 
                    opacity: isUploading ? 0.5 : 1,
                    pointerEvents: isUploading ? 'none' : 'auto'
                  }}
                >
                  <img src="/images/icons/edt_btn.png" alt="Edit" />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept="image/*"
                  disabled={isUploading}
                  multiple={false}
                />
              </div>

              {/* Upload Status Messages */}
              {uploadError && (
                <div className="upload-message upload-error">
                  <span className="error-icon">⚠️</span>
                  <span>{uploadError}</span>
                  <button 
                    className="close-message" 
                    onClick={() => setUploadError('')}
                  >
                    ×
                  </button>
                </div>
              )}

              {uploadSuccess && (
                <div className="upload-message upload-success">
                  <span className="success-icon">✅</span>
                  <span>Cập nhật ảnh đại diện thành công!</span>
                  <button 
                    className="close-message" 
                    onClick={() => setUploadSuccess(false)}
                  >
                    ×
                  </button>
                </div>
              )}

              <div className="user-details">
                <h2 className="user-name">{getUserDisplayName()}</h2>

                {!user?.isAppUser &&(<div className="user-info-grid">
                    <div className="info-item">
                      <span className="info-label">Ngày chính thức tham gia: {user?.createdAt}</span>
                    </div>
                </div>)}
                <div className="settings-content">

                <div className="settings-menu">
                  {/* Referral Code - styled like menu items */}
                  {user?.referralCode && (
                    <div
                      className="settings-menu-item"
                      onClick={handleCopyReferralCode}
                    >
                      <div className="menu-item-content">
                        <p>
                          {copySuccess
                            ? `✓ Đã sao chép mã: ${user.referralCode}`
                            : `Mã giới thiệu: ${user.referralCode} (Bấm vào đây để copy)`
                          }
                        </p>
                      </div>
                      <div className="edt_btn_badge"><img src="/images/icons/icon_copy.png" alt="Copy" /></div>
                    </div>
                  )}

                  {!user?.isAppUser && (<div
                    className={`settings-menu-item ${hasVerifiedPhone ? 'disabled' : ''}`}
                    onClick={hasVerifiedPhone ? undefined : () => setShowPhoneVerify(true)}
                  >
                    <div className="menu-item-content">
                      <p>
                        {hasVerifiedPhone
                          ? `Số điện thoại: ${user?.verifiedPhoneNumber}`
                          : 'Cập nhật số điện thoại'
                        }
                      </p>
                    </div>
                    {!hasVerifiedPhone && (
                      <div className="edt_btn_badge"><img src="/images/icons/edt_btn.png" alt="Verified" /></div>
                    )}
                  </div>)}

                  <div
                    className="settings-menu-item"
                    onClick={() => setShowUserInfo(true)}
                  >
                    <div className="menu-item-content">
                      <p>Cập nhật thông tin khác</p>
                    </div>
                    <div className="edt_btn_badge"><img src="/images/icons/edt_btn.png" alt="Verified" /></div>
                  </div>

                  {/* Copy referral link - only show when phone is verified */}
                  {/* {hasVerifiedPhone && (
                    <div 
                      className="settings-menu-item-2nd"
                      onClick={handleCopyReferralLink}
                    >
                      <div className="menu-item-content">
                        <h2>Chia sẻ link để mời bạn bè tham gia</h2>
                        <div className='menu-item-content-row'>
                          <p>
                            {copySuccess 
                              ? '✓ Đã sao chép link!' 
                              : 'Sao chép link gửi bạn bè để nhận thưởng'
                            }
                          </p>
                          <div className="arrow-icon"><img src='/images/icons/icon_copy.png' alt="Arrow" /></div>
                        </div>
                        
                      </div>
                    </div>
                  )}

                  <div 
                    className={`settings-menu-item-2nd`}
                    onClick={hasReferrer ? undefined : () => setShowReferralCode(true)}
                  >
                    <div className="menu-item-content">
                      <h3>Nhập mã giới thiệu</h3>
                      <p>
                        Nhập số điện thoại người mời để nhận thưởng đặc biệt
                      </p>
                      <button>Nhập số điện thoại người mời</button>
                    </div>
                  </div> */}
                </div>

                {/* Terms of Service link */}
                {/* <div 
                  style={{ marginTop: '16px' }}
                  className="settings-menu-item"
                  onClick={() => navigate('/terms')}
                >
                  <div className="menu-item-content">
                    <p>Điều khoản sử dụng</p>
                  </div>
                  <div className="arrow-icon">→</div>
                </div> */}

                {/* Logout Button */}
                <div className="logout-section">
                  <button 
                    className="logout-button"
                    onClick={handleLogout}
                  >
                    Đăng xuất
                  </button>
                </div>
              </div>
              </div>
              </div>

              {/* Settings Menu */}
              
            </div>
            
          </div>
          {/* User Info Section */}
          
        </div>

        {/* Phone Verify Modal */}
        <PhoneVerifyModal
          isOpen={showPhoneVerify}
          onClose={() => setShowPhoneVerify(false)}
          onVerificationSuccess={handlePhoneVerifySuccess}
        />

        {/* User Info Modal */}
        <UserInfoModal
          isOpen={showUserInfo}
          onClose={() => setShowUserInfo(false)}
          onUpdateSuccess={handleUserInfoSuccess}
        />

        {/* Referral Code Modal */}
        <ReferralCodeModal
          isOpen={showReferralCode}
          onClose={() => setShowReferralCode(false)}
          onSuccess={handleReferralCodeSuccess}
        />

        {/* Referral List Modal */}
        <ReferralListModal
          visible={showReferralList}
          onClose={() => setShowReferralList(false)}
        />

        {/* Referral Link Modal - for manual copy fallback */}
        {showReferralLink && (
          <div className="settings-modal-overlay" onClick={() => setShowReferralLink(false)}>
            <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="settings-modal-header">
                <h2>Link mời bạn bè</h2>
                <button className="close-button" onClick={() => setShowReferralLink(false)}>
                  ×
                </button>
              </div>
              <div className="settings-modal-body">
                <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                  Vui lòng sao chép link dưới đây để chia sẻ với bạn bè:
                </p>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '16px',
                  wordBreak: 'break-all',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }} className="referral-link-text">
                  {referralLink}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={async () => {
                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(referralLink);
                          setCopySuccess(true);
                          setShowReferralLink(false);
                          setTimeout(() => setCopySuccess(false), 2000);
                        } else {
                          // Select text for manual copy
                          const selection = window.getSelection();
                          const range = document.createRange();
                          const linkElement = document.querySelector('.referral-link-text');
                          if (linkElement) {
                            range.selectNodeContents(linkElement);
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                          }
                        }
                      } catch (error) {
                        console.error('Manual copy failed:', error);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Sao chép
                  </button>
                  <button
                    onClick={() => {
                      window.open(referralLink, '_blank');
                      setShowReferralLink(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Mở link
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </BaseLayout>
    </div>
  );
};

export default SettingsPage;
